import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiCheck, 
  FiArrowRight, 
  FiArrowLeft,
  FiMail,
  FiAlertTriangle,
  FiLoader,
  FiBookOpen,
  FiBriefcase,
  FiHeart,
  FiTarget,
  FiUser,
  FiAward
} from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import { isEmailValid } from "../../utils/validationUtils";
import { handleNetworkError } from "../../utils/errorUtils";
import authService from "../../services/authService";
import {
  AuthLayout,
  AuthInput,
  AuthButton,
  InstitutionVerifier,
  HierarchicalFieldSelect,
  CategorizedRoleSelect,
  GoalsCheckboxGroup,
  InterestsSelector,
  ResearchInterestsInput,
} from "./components";
import styles from "./Auth.module.css";

// Total steps in the onboarding flow
const TOTAL_STEPS = 7;

const Onboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { saveOnboardingData, user, isAuthenticated, loading: authLoading } = useUserContext();
  
  // Current step
  const [step, setStep] = useState(1);
  
  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  
  // Academic data from API
  const [academicData, setAcademicData] = useState({
    fields: null,
    roles: null,
    interests: null,
    goals: null,
  });
  
  // Form data - matches new backend structure
  const [formData, setFormData] = useState({
    // Institution
    institutionEmail: user?.email || "",
    institutionName: "",
    detectedInstitution: null,
    
    // Field (hierarchical)
    fieldCategory: "",
    field: "",
    subField: "",
    isCustomField: false,
    customFieldValue: "",
    
    // Professional Role
    professionalRole: "",
    professionalRoleCategory: "",
    isCustomRole: false,
    customRoleValue: "",
    
    // Interests
    interests: [],
    researchInterests: [],
    
    // Goals
    goals: [],
    customGoalText: "",
    goalsText: "",
  });
  
  const [errors, setErrors] = useState({});

  const from = location.state?.from || "/";

  // Handle institution verification result
  const handleInstitutionVerification = useCallback((result) => {
    if (result?.institution) {
      setFormData(prev => ({
        ...prev,
        detectedInstitution: result.institution,
        institutionName: result.institution.name,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        detectedInstitution: null,
      }));
    }
  }, []);

  // Fetch academic data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        const data = await authService.fetchAcademicData();
        setAcademicData(data);
      } catch (error) {
        setErrors({ general: "Failed to load form data. Please refresh the page." });
      } finally {
        setIsLoadingData(false);
      }
    };

    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  // Redirect if not authenticated (after all hooks)
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/signin" state={{ from: "/onboarding" }} />;
  }

  // Validate current step
  const validateStep = () => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Institution Email
        if (!formData.institutionEmail) {
          newErrors.institutionEmail = "Institution email is required";
        } else if (!isEmailValid(formData.institutionEmail)) {
          newErrors.institutionEmail = "Please enter a valid email address";
        }
        break;
        
      case 2: // Field Selection
        if (!formData.isCustomField) {
          if (!formData.fieldCategory) {
            newErrors.field = "Please select a category";
          } else if (!formData.field) {
            newErrors.field = "Please select your field";
          }
        } else if (!formData.customFieldValue?.trim()) {
          newErrors.field = "Please enter your field";
        }
        break;
        
      case 3: // Role Selection
        if (!formData.isCustomRole) {
          if (!formData.professionalRole) {
            newErrors.role = "Please select your role";
          }
        } else if (!formData.customRoleValue?.trim()) {
          newErrors.role = "Please enter your role";
        }
        break;
        
      case 4: // Interests
        if (formData.interests.length === 0) {
          newErrors.interests = "Please select at least one interest";
        }
        break;
        
      case 5: // Research Interests
        // Optional - no validation required
        break;
        
      case 6: // Goals
        if (formData.goals.length === 0) {
          newErrors.goals = "Please select at least one goal";
        }
        // Validate custom goal has text if selected
        const customGoal = formData.goals.find(g => g.type === 'custom');
        if (customGoal && !customGoal.customText?.trim()) {
          newErrors.goals = "Please describe your custom goal";
        }
        break;
        
      case 7: // Review - no validation
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const handleNext = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
      setErrors({});
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate("/signin", { state: { from: "/onboarding" } });
      return;
    }

    if (!validateStep()) return;

    setIsSubmitting(true);
    try {
      // Build the onboarding data object matching backend structure
      const onboardingData = {
        // Institution
        institutionEmail: formData.institutionEmail,
        institutionName: formData.institutionName || formData.detectedInstitution?.name,
        
        // Field (hierarchical)
        fieldCategory: formData.isCustomField ? null : formData.fieldCategory,
        field: formData.isCustomField ? formData.customFieldValue : formData.field,
        subField: formData.isCustomField ? null : formData.subField,
        isCustomField: formData.isCustomField,
        
        // Professional Role
        professionalRole: formData.isCustomRole ? formData.customRoleValue : formData.professionalRole,
        professionalRoleCategory: formData.isCustomRole ? 'independent' : formData.professionalRoleCategory,
        isCustomRole: formData.isCustomRole,
        
        // Interests
        interests: formData.interests,
        researchInterests: formData.researchInterests,
        
        // Goals
        goals: formData.goals,
        goalsText: formData.goalsText,
      };

      await saveOnboardingData(onboardingData);
      setIsComplete(true);

      setTimeout(() => {
        navigate(from);
      }, 2000);
    } catch (error) {
      const processedError = handleNetworkError(error);
      setErrors({ general: processedError.message });
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isComplete) {
    return (
      <AuthLayout>
        <motion.div
          className={styles['au-success']}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className={styles['au-success__icon']}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <FiCheck size={32} />
          </motion.div>
          <h2 className={styles['au-success__title']}>Welcome aboard!</h2>
          <p className={styles['au-success__text']}>Redirecting to your personalized feed...</p>
        </motion.div>
      </AuthLayout>
    );
  }

  // Loading state for academic data
  if (isLoadingData) {
    return (
      <AuthLayout
        title="Setting Up Your Profile"
        subtitle="Loading personalization options..."
      >
        <div className={styles['au-loading']}>
          <FiLoader className={styles['au-loading__spinner']} size={32} />
          <p className={styles['au-loading__text']}>Preparing your onboarding experience...</p>
        </div>
      </AuthLayout>
    );
  }

  // Step content renderer
  const renderStepContent = () => {
    const stepVariants = {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 },
    };

    switch (step) {
      case 1: // Institution Email
        return (
          <motion.div key="step1" {...stepVariants} transition={{ duration: 0.3 }}>
            <AuthInput
              icon={FiMail}
              label="Institution/Organization Email"
              type="email"
              name="institutionEmail"
              value={formData.institutionEmail}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, institutionEmail: e.target.value }));
                if (errors.institutionEmail) setErrors(prev => ({ ...prev, institutionEmail: undefined }));
              }}
              placeholder="e.g., name@university.edu"
              error={errors.institutionEmail}
              disabled={isSubmitting}
            />
            
            <InstitutionVerifier
              email={formData.institutionEmail}
              onVerificationComplete={handleInstitutionVerification}
              onManualInstitutionChange={(name) => setFormData(prev => ({ ...prev, institutionName: name }))}
              manualInstitutionName={formData.institutionName}
              disabled={isSubmitting}
            />
          </motion.div>
        );

      case 2: // Field Selection
        return (
          <motion.div key="step2" {...stepVariants} transition={{ duration: 0.3 }}>
            <HierarchicalFieldSelect
              fieldsData={academicData.fields}
              fieldCategory={formData.fieldCategory}
              field={formData.field}
              subField={formData.subField}
              onCategoryChange={(val) => setFormData(prev => ({ ...prev, fieldCategory: val }))}
              onFieldChange={(val) => setFormData(prev => ({ ...prev, field: val }))}
              onSubFieldChange={(val) => setFormData(prev => ({ ...prev, subField: val }))}
              isCustomField={formData.isCustomField}
              onCustomFieldToggle={(val) => setFormData(prev => ({ ...prev, isCustomField: val }))}
              customFieldValue={formData.customFieldValue}
              onCustomFieldChange={(val) => setFormData(prev => ({ ...prev, customFieldValue: val }))}
              error={errors.field}
              disabled={isSubmitting}
            />
          </motion.div>
        );

      case 3: // Role Selection
        return (
          <motion.div key="step3" {...stepVariants} transition={{ duration: 0.3 }}>
            <CategorizedRoleSelect
              rolesData={academicData.roles}
              selectedRole={formData.professionalRole}
              selectedCategory={formData.professionalRoleCategory}
              onRoleSelect={(role, category) => setFormData(prev => ({ 
                ...prev, 
                professionalRole: role,
                professionalRoleCategory: category,
              }))}
              isCustomRole={formData.isCustomRole}
              onCustomRoleToggle={(val) => setFormData(prev => ({ ...prev, isCustomRole: val }))}
              customRoleValue={formData.customRoleValue}
              onCustomRoleChange={(val) => setFormData(prev => ({ ...prev, customRoleValue: val }))}
              error={errors.role}
              disabled={isSubmitting}
            />
          </motion.div>
        );

      case 4: // Interests
        return (
          <motion.div key="step4" {...stepVariants} transition={{ duration: 0.3 }}>
            <InterestsSelector
              interestsData={academicData.interests}
              selectedInterests={formData.interests}
              onInterestToggle={(interests) => {
                setFormData(prev => ({ ...prev, interests }));
                if (errors.interests) setErrors(prev => ({ ...prev, interests: undefined }));
              }}
              maxInterests={20}
              label="Your Interests"
              placeholder="Search interests..."
              error={errors.interests}
              disabled={isSubmitting}
            />
          </motion.div>
        );

      case 5: // Research Interests
        return (
          <motion.div key="step5" {...stepVariants} transition={{ duration: 0.3 }}>
            <ResearchInterestsInput
              selectedInterests={formData.researchInterests}
              onInterestsChange={(interests) => setFormData(prev => ({ ...prev, researchInterests: interests }))}
              maxInterests={15}
              suggestions={academicData.interests?.flatList || []}
              label="Research Interests"
              placeholder="Type your research focus areas..."
              error={errors.researchInterests}
              disabled={isSubmitting}
            />
          </motion.div>
        );

      case 6: // Goals
        return (
          <motion.div key="step6" {...stepVariants} transition={{ duration: 0.3 }}>
            <GoalsCheckboxGroup
              goalsData={academicData.goals}
              selectedGoals={formData.goals}
              onGoalToggle={(goals) => {
                setFormData(prev => ({ ...prev, goals }));
                if (errors.goals) setErrors(prev => ({ ...prev, goals: undefined }));
              }}
              customGoalText={formData.customGoalText}
              onCustomGoalTextChange={(text) => setFormData(prev => ({ ...prev, customGoalText: text }))}
              goalsText={formData.goalsText}
              onGoalsTextChange={(text) => setFormData(prev => ({ ...prev, goalsText: text }))}
              error={errors.goals}
              disabled={isSubmitting}
            />
          </motion.div>
        );

      case 7: // Review
        return (
          <motion.div key="step7" {...stepVariants} transition={{ duration: 0.3 }}>
            {errors.general && (
              <div className={`${styles['au-alert']} ${styles['au-alert--error']}`}>
                <FiAlertTriangle className={styles['au-alert__icon']} />
                <span>{errors.general}</span>
              </div>
            )}
            
            <div className={styles['au-summary']}>
              <div className={styles['au-summary__item']}>
                <span className={styles['au-summary__label']}>
                  <FiMail size={14} /> Institution Email
                </span>
                <span className={styles['au-summary__value']}>{formData.institutionEmail}</span>
              </div>
              
              {formData.institutionName && (
                <div className={styles['au-summary__item']}>
                  <span className={styles['au-summary__label']}>
                    <FiAward size={14} /> Institution
                  </span>
                  <span className={styles['au-summary__value']}>{formData.institutionName}</span>
                </div>
              )}
              
              <div className={styles['au-summary__item']}>
                <span className={styles['au-summary__label']}>
                  <FiBookOpen size={14} /> Field
                </span>
                <span className={styles['au-summary__value']}>
                  {formData.isCustomField 
                    ? formData.customFieldValue 
                    : `${formData.field}${formData.subField ? ` • ${formData.subField}` : ''}`
                  }
                </span>
              </div>
              
              <div className={styles['au-summary__item']}>
                <span className={styles['au-summary__label']}>
                  <FiBriefcase size={14} /> Role
                </span>
                <span className={styles['au-summary__value']}>
                  {formData.isCustomRole ? formData.customRoleValue : formData.professionalRole}
                </span>
              </div>
              
              <div className={styles['au-summary__item']}>
                <span className={styles['au-summary__label']}>
                  <FiHeart size={14} /> Interests
                </span>
                <div className={styles['au-summary__chips']}>
                  {formData.interests.slice(0, 5).map((interest) => (
                    <span key={interest} className={styles['au-summary__chip']}>
                      {interest}
                    </span>
                  ))}
                  {formData.interests.length > 5 && (
                    <span className={styles['au-summary__chip']}>
                      +{formData.interests.length - 5} more
                    </span>
                  )}
                </div>
              </div>
              
              {formData.researchInterests.length > 0 && (
                <div className={styles['au-summary__item']}>
                  <span className={styles['au-summary__label']}>
                    <FiUser size={14} /> Research Focus
                  </span>
                  <div className={styles['au-summary__chips']}>
                    {formData.researchInterests.slice(0, 3).map((interest) => (
                      <span key={interest} className={styles['au-summary__chip']}>
                        {interest}
                      </span>
                    ))}
                    {formData.researchInterests.length > 3 && (
                      <span className={styles['au-summary__chip']}>
                        +{formData.researchInterests.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <div className={styles['au-summary__item']}>
                <span className={styles['au-summary__label']}>
                  <FiTarget size={14} /> Goals
                </span>
                <span className={styles['au-summary__value']}>
                  {formData.goals.length} goal{formData.goals.length !== 1 ? 's' : ''} selected
                </span>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  // Step titles and subtitles
  const getStepInfo = () => {
    switch (step) {
      case 1:
        return {
          title: "Welcome to ResearchPro!",
          subtitle: "Let's verify your institutional affiliation",
          icon: <FiMail size={20} />,
        };
      case 2:
        return {
          title: "Your Field of Study",
          subtitle: "This helps us connect you with relevant research",
          icon: <FiBookOpen size={20} />,
        };
      case 3:
        return {
          title: "Your Professional Role",
          subtitle: "Tell us about your current position",
          icon: <FiBriefcase size={20} />,
        };
      case 4:
        return {
          title: "Your Interests",
          subtitle: "Select topics you're interested in (up to 20)",
          icon: <FiHeart size={20} />,
        };
      case 5:
        return {
          title: "Research Focus",
          subtitle: "Add your specific research topics (optional)",
          icon: <FiUser size={20} />,
        };
      case 6:
        return {
          title: "Your Goals",
          subtitle: "What do you hope to achieve on ResearchPro?",
          icon: <FiTarget size={20} />,
        };
      case 7:
        return {
          title: "Review Your Profile",
          subtitle: "Please confirm your information",
          icon: <FiCheck size={20} />,
        };
      default:
        return { title: "", subtitle: "", icon: null };
    }
  };

  const stepInfo = getStepInfo();

  return (
    <AuthLayout
      title={stepInfo.title}
      subtitle={stepInfo.subtitle}
    >
      {/* Step Content */}
      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>

      {/* Navigation Actions */}
      <div className={styles['au-step-actions']}>
        {step > 1 && (
          <AuthButton
            variant="secondary"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            <FiArrowLeft size={16} />
            Back
          </AuthButton>
        )}

        {step < TOTAL_STEPS ? (
          <AuthButton
            variant="primary"
            onClick={handleNext}
            disabled={isSubmitting}
            style={step === 1 ? { width: '100%' } : {}}
          >
            {step === 5 ? 'Skip' : 'Next'}
            <FiArrowRight size={16} />
          </AuthButton>
        ) : (
          <AuthButton
            variant="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? "Completing Setup..." : "Complete Setup"}
          </AuthButton>
        )}
      </div>

      {/* Progress Indicator */}
      <div className={styles['au-progress']}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s, index) => (
          <React.Fragment key={s}>
            {index > 0 && (
              <div 
                className={`${styles['au-progress__line']} ${
                  s <= step ? styles['au-progress__line--completed'] : ''
                }`}
              />
            )}
            <div
              className={`${styles['au-progress__step']} ${
                s === step 
                  ? styles['au-progress__step--active'] 
                  : s < step 
                  ? styles['au-progress__step--completed'] 
                  : ''
              }`}
            />
          </React.Fragment>
        ))}
      </div>
    </AuthLayout>
  );
};

export default Onboarding;
